/**
 * PPR-6 · 上架中心「同行站」页签（促销页部分）：促销页横幅 + 促销页入口。
 * 契约：designs/mockups/channel-center/上架中心-同行站与各渠道显示-样稿.html（tag listing-partner-site-mockup-v1）+
 *       designs/mockups/channel-partners/公司站促销页-样稿.html（tag partner-portal-promo-mockup-v1）。
 * 做法照手机站首页横幅（MobileSiteConsole）：拖动排序 + 上 / 下移兜底、整组有序保存。
 *   · 促销页横幅：最多 5 张；图片**从图片库选**（这里不上传新图）；选点了跳哪个产品（只列同行站在展示的）；上线 / 下线
 *   · 促销页入口：名称 + 按出发城市 / 按标签 + 规则值；每个入口显示「当前 N 个产品」，0 个 = 前台「即将上线」
 * 接口：GET/PUT /api/v1/partner-site/promo-banners · /promo-entries（挂 listing.mappings.view / manage）。
 * 本页签的「同行站展示清单（开关 / 排序 / 没展示原因）」是 PPR-3 的内容，会加在这两块上面。
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, GripVertical, Plus, X } from "lucide-react";
import { Can } from "@/auth/permissions";
import { ApiError } from "@/services/api";
import { getAsset } from "@/services/mediaLibrary";
import { MediaPickModal } from "@/components/media/MediaPickModal";
import {
  PROMO_QK, RULE_TYPE_LABEL, getPromoBanners, getPromoEntries, listPartnerSiteProducts, putPromoBanners, putPromoEntries,
  type PromoBanner, type PromoEntry, type PromoRuleType,
} from "@/services/partnerPromo";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : "保存失败，请重试");

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = { green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", gray: "bg-slate-100 text-slate-600", red: "bg-rose-50 text-rose-600" }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />{children}
    </span>
  );
}

/** 拖动排序（原生 HTML5 拖放）+ 上 / 下移按钮兜底（键盘 / 触屏也能排）——同手机站首页横幅的做法 */
function useReorder<T>(items: T[], setItems: (v: T[]) => void) {
  const dragFrom = useRef<number | null>(null);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = items.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setItems(next);
  };
  const dragProps = (i: number) => ({
    draggable: true,
    onDragStart: () => { dragFrom.current = i; },
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDrop: (e: React.DragEvent) => { e.preventDefault(); if (dragFrom.current != null) move(dragFrom.current, i); dragFrom.current = null; },
  });
  return { move, dragProps };
}

const card = "rounded-2xl border border-slate-200 bg-white p-5";

// ───────────────────────────── 促销页横幅 ─────────────────────────────

function PromoBannersCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: PROMO_QK.banners, queryFn: getPromoBanners });
  const prodsQ = useQuery({ queryKey: PROMO_QK.products, queryFn: listPartnerSiteProducts });
  const [draft, setDraft] = useState<PromoBanner[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const max = q.data?.max ?? 5;
  const items = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  // 跳转产品只能选「同行站在展示」的（和后台清单同一个判断）
  const showing = useMemo(() => (prodsQ.data?.items ?? []).filter((p) => p.display_status === "showing"), [prodsQ.data]);
  const noImage = items.some((b) => !b.image_media_id);
  const saveMut = useMutation({
    mutationFn: () => putPromoBanners(items.map((b) => ({ image_media_id: b.image_media_id, title: b.title.trim(), subtitle: b.subtitle?.trim() || null, link_product_id: b.link_product_id || null, active: b.active }))),
    onSuccess: (d) => { qc.setQueryData(PROMO_QK.banners, d); setDraft(null); setErr(null); },
    onError: (e) => setErr(errMsg(e)),
  });
  const edit = (i: number, patch: Partial<PromoBanner>) => setDraft(items.map((b, k) => (k === i ? { ...b, ...patch } : b)));

  const onPicked = async (ids: string[]) => {
    setPicking(false);
    const room = max - items.length;
    const assets = await Promise.all(ids.slice(0, Math.max(0, room)).map((id) => getAsset(id).catch(() => null)));
    const add: PromoBanner[] = assets.filter((a): a is NonNullable<typeof a> => a !== null)
      .map((a) => ({ image_media_id: a.id, image_url: a.url, title: "", subtitle: null, link_product_id: null, active: true }));
    if (add.length) setDraft([...items, ...add]);
  };

  if (q.isLoading) return <section className={card}><p className="text-sm text-slate-400">加载中…</p></section>;
  if (q.isError) return <section className={card}><button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button></section>;

  return (
    <section className={card} data-testid="promo-banners">
      <h3 className="text-[15px] font-semibold text-slate-900">促销页横幅 <span className="ml-1 text-xs font-normal text-slate-400">同行登录后的促销页 + 官网首页顶部轮播（同一组图）</span></h3>
      <p className="mt-1 text-[12.5px] text-slate-500">最多 {max} 张 · 拖动排顺序 · 图片从「图片库」选（要新图先去图片库上传）· 每张可选点了跳哪个产品（只列同行站在展示的）· 上线 / 下线 · 改完点「保存」</p>
      <div className="mt-3 grid grid-cols-2 gap-3 xl:grid-cols-3">
        {items.map((b, i) => (
          <div key={`${b.id ?? "new"}-${b.image_media_id}-${i}`} {...dragProps(i)} data-testid="promo-banner-card"
            className={`flex flex-col overflow-hidden rounded-[10px] border bg-white text-[12px] ${b.image_missing || b.link_visible === false ? "border-rose-300" : "border-slate-200"}`}>
            <div className="relative aspect-[3/1] bg-slate-800 bg-cover bg-center" style={b.image_url ? { backgroundImage: `url('${b.image_url.replace(/'/g, "%27")}')` } : undefined}>
              <span className="absolute left-1 top-1 rounded bg-slate-900/70 px-1.5 text-[11px] text-white">{i + 1}</span>
              {b.image_missing && <span className="absolute inset-0 flex items-center justify-center bg-rose-900/60 text-[12px] font-medium text-white" data-testid="banner-image-missing">图片库里的图没了 / 已停用 · 前台不出</span>}
            </div>
            <div className="flex flex-col gap-1.5 p-2 text-slate-600">
              <div className="flex items-center gap-1 text-slate-400">
                <GripVertical size={13} className="cursor-grab" /><span>拖动</span>
                <Can perm="listing.mappings.manage">
                  <span className="ml-auto flex gap-0.5">
                    <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                    <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                    <button type="button" aria-label="移除横幅" onClick={() => setDraft(items.filter((_, k) => k !== i))} className="rounded p-0.5 hover:bg-rose-50 hover:text-rose-600"><X size={12} /></button>
                  </span>
                </Can>
              </div>
              <input aria-label="标题" value={b.title} maxLength={60} placeholder="标题（选填）" onChange={(e) => edit(i, { title: e.target.value })}
                className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <input aria-label="副标题" value={b.subtitle ?? ""} maxLength={120} placeholder="副标题（选填）" onChange={(e) => edit(i, { subtitle: e.target.value })}
                className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <label className="flex flex-col gap-0.5">点了跳到：
                <select aria-label="跳转产品" value={b.link_product_id ?? ""} onChange={(e) => edit(i, { link_product_id: e.target.value || null })}
                  className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">不跳转</option>
                  {b.link_product_id && !showing.some((p) => p.product_id === b.link_product_id) && <option value={b.link_product_id}>{b.link_product_name ?? b.link_product_code ?? "（产品）"}（同行站没在展示）</option>}
                  {showing.map((p) => <option key={p.product_id} value={p.product_id}>{p.name_cn}（{p.code}）</option>)}
                </select>
              </label>
              {b.link_visible === false && <span className="text-[11.5px] text-rose-600" data-testid="banner-link-hidden">跳转的产品同行站没在展示：前台这张图不带跳转</span>}
              <button type="button" onClick={() => edit(i, { active: !b.active })} className="self-start" aria-pressed={b.active}>
                {b.active ? <Pill tone="green">上线</Pill> : <Pill tone="gray">下线</Pill>}
              </button>
            </div>
          </div>
        ))}
        {items.length < max && (
          <Can perm="listing.mappings.manage">
            <button type="button" onClick={() => setPicking(true)} data-testid="promo-banner-add"
              className="flex min-h-[150px] flex-col items-center justify-center rounded-[10px] border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600">
              <Plus size={16} />从图片库选图<span className="text-[11px]">建议 3:1 横幅 · 宽 ≥ 1600 px</span>
            </button>
          </Can>
        )}
      </div>
      {items.length === 0 && <p className="mt-2 text-[12.5px] text-amber-700" data-testid="promo-banners-empty">还没有横幅：促销页顶部不显示轮播（入口和产品卡照常）。</p>}
      {err && <p className="mt-2 text-xs text-rose-600" role="alert">{err}</p>}
      <Can perm="listing.mappings.manage">
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setErr(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
          <button type="button" data-testid="promo-banners-save" disabled={!dirty || saveMut.isPending || noImage} onClick={() => saveMut.mutate()}
            className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
        </div>
      </Can>
      <MediaPickModal open={picking} onClose={() => setPicking(false)} slot="gallery" productLabel="促销页横幅" existingIds={new Set(items.map((b) => b.image_media_id))}
        tripAttractionIds={[]} onConfirm={(ids) => { void onPicked(ids); }} />
    </section>
  );
}

// ───────────────────────────── 促销页入口 ─────────────────────────────

function PromoEntriesCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: PROMO_QK.entries, queryFn: getPromoEntries });
  const [draft, setDraft] = useState<PromoEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const max = q.data?.max ?? 12;
  const items = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  const invalid = items.some((e) => !e.name.trim() || !e.rule_value.trim());
  const saveMut = useMutation({
    mutationFn: () => putPromoEntries(items.map((e) => ({ name: e.name.trim(), rule_type: e.rule_type, rule_value: e.rule_value.trim(), active: e.active }))),
    onSuccess: (d) => { qc.setQueryData(PROMO_QK.entries, d); setDraft(null); setErr(null); },
    onError: (e) => setErr(errMsg(e)),
  });
  const edit = (i: number, patch: Partial<PromoEntry>) => setDraft(items.map((e, k) => (k === i ? { ...e, ...patch } : e)));
  const add = () => setDraft([...items, { name: "", rule_type: "departure_city", rule_value: "", active: true }]);

  if (q.isLoading) return <section className={card}><p className="text-sm text-slate-400">加载中…</p></section>;
  if (q.isError) return <section className={card}><button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button></section>;

  return (
    <section className={card} data-testid="promo-entries">
      <h3 className="text-[15px] font-semibold text-slate-900">促销页入口 <span className="ml-1 text-xs font-normal text-slate-400">促销页上的入口按钮；点了滚到对应分组，每组每行 5 张产品卡</span></h3>
      <p className="mt-1 text-[12.5px] text-slate-500">
        分组按产品的<b>出发城市</b>或<b>标签</b>自动归（规则值要和产品里的写法一致，不分大小写）· 组内顺序 = 同行站排序 · 只出「同行站」开关打开的产品 ·
        <b>当前 0 个产品的入口，前台显示「即将上线」</b>（有产品后自动出现）· 最多 {max} 个
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {items.map((e, i) => (
          <div key={`${e.id ?? "new"}-${i}`} {...dragProps(i)} data-testid="promo-entry-row" className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
            <GripVertical size={14} className="cursor-grab text-slate-400" />
            <span className="w-5 text-center text-xs text-slate-400">{i + 1}</span>
            <input aria-label="入口名称" value={e.name} maxLength={30} placeholder="入口名称，如 洛杉矶出发" onChange={(ev) => edit(i, { name: ev.target.value })}
              className="w-44 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <select aria-label="规则类型" value={e.rule_type} onChange={(ev) => edit(i, { rule_type: ev.target.value as PromoRuleType })} className="rounded border border-slate-300 px-2 py-1 text-sm">
              {(Object.keys(RULE_TYPE_LABEL) as PromoRuleType[]).map((k) => <option key={k} value={k}>{RULE_TYPE_LABEL[k]}</option>)}
            </select>
            <input aria-label="规则值" value={e.rule_value} maxLength={60} placeholder={e.rule_type === "tag" ? "标签，如 门票 / 秀票" : "出发城市，如 Los Angeles"} onChange={(ev) => edit(i, { rule_value: ev.target.value })}
              className="w-52 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <button type="button" onClick={() => edit(i, { active: !e.active })} aria-pressed={e.active}>{e.active ? <Pill tone="green">上线</Pill> : <Pill tone="gray">下线</Pill>}</button>
            {e.id && !dirty && (
              (e.product_count ?? 0) === 0
                ? <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[12px] font-medium text-amber-700" data-testid="entry-coming-soon">当前 0 个产品 · 前台「即将上线」</span>
                : <span className="text-[12px] text-slate-500" data-testid="entry-count">当前 {e.product_count} 个产品</span>
            )}
            <Can perm="listing.mappings.manage">
              <span className="ml-auto flex gap-0.5 text-slate-400">
                <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-1 hover:bg-slate-100"><ArrowUp size={13} /></button>
                <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-1 hover:bg-slate-100"><ArrowDown size={13} /></button>
                <button type="button" aria-label="删除入口" onClick={() => setDraft(items.filter((_, k) => k !== i))} className="rounded p-1 hover:bg-rose-50 hover:text-rose-600"><X size={13} /></button>
              </span>
            </Can>
          </div>
        ))}
        {items.length === 0 && <p className="text-[12.5px] text-amber-700" data-testid="promo-entries-empty">还没有入口：促销页只有横幅，没有产品分组。公司站建议录 6 个：洛杉矶 / 旧金山 / 拉斯维加斯 / 西雅图 / 佩吉出发（按出发城市）+ 门票 / 秀票（按标签）。</p>}
        {items.length < max && (
          <Can perm="listing.mappings.manage">
            <button type="button" onClick={add} data-testid="promo-entry-add" className="self-start rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-[13px] text-slate-500 hover:border-blue-400 hover:text-blue-600">+ 添加入口</button>
          </Can>
        )}
      </div>
      {err && <p className="mt-2 text-xs text-rose-600" role="alert">{err}</p>}
      <Can perm="listing.mappings.manage">
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setErr(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
          <button type="button" data-testid="promo-entries-save" disabled={!dirty || saveMut.isPending || invalid} onClick={() => saveMut.mutate()}
            className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
        </div>
      </Can>
    </section>
  );
}

/** 「同行站」页签（目前只有促销页两块；PPR-3 把「展示清单」加在这两块上面）。 */
export function PartnerSiteConsole() {
  return (
    <div className="mt-5 flex flex-col gap-4" data-testid="partner-site-console">
      <PromoEntriesCard />
      <PromoBannersCard />
    </div>
  );
}
